import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useToast } from '../../hooks/use-toast';
import { adminApi } from '../../lib/adminApi';
import { Check, X, GripVertical } from 'lucide-react';
import { Dropzone } from './Dropzone';
import { SortableList } from './SortableList';
import { ItemActions } from './ItemActions';
import { PreviewModal } from './PreviewModal';

const CATEGORIES = ['Events', 'Programmes', 'Community', 'Volunteers'];

const GalleryManager = () => {
  const [gallery, setGallery] = useState([]);
  const [editing, setEditing] = useState(null);
  const [previewing, setPreviewing] = useState(null);
  const { toast } = useToast();

  const fetchGallery = async () => {
    try {
      const res = await adminApi.get('/gallery');
      setGallery(res.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    fetchGallery();
  }, []);

  const handleUpload = async (url, file) => {
    const item = {
      url,
      category: 'Events',
      title: (file.name || 'Untitled').split('.')[0],
      order: gallery.length,
      published: true,
    };
    await adminApi.post('/gallery', item);
    fetchGallery();
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this image?')) return;
    try {
      await adminApi.delete(`/gallery/${id}`);
      toast({ title: 'Deleted' });
      fetchGallery();
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to delete', variant: 'destructive' });
    }
  };

  const saveEdit = async () => {
    try {
      await adminApi.put(`/gallery/${editing.id}`, editing);
      toast({ title: 'Saved' });
      setEditing(null);
      fetchGallery();
    } catch (err) {
      toast({ title: 'Error', description: 'Save failed', variant: 'destructive' });
    }
  };

  const handleReorder = async (reordered) => {
    setGallery(reordered);
    try {
      await adminApi.put('/gallery/reorder', { ids: reordered.map((r) => r.id) });
    } catch (e) {
      toast({ title: 'Error', description: 'Reorder failed', variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-6" data-testid="gallery-manager">
      <h2 className="text-2xl font-bold">Gallery</h2>

      <Card>
        <CardContent className="p-6">
          <Dropzone
            multiple
            onUpload={handleUpload}
            label="Drag and drop images here, or click to browse"
          />
        </CardContent>
      </Card>

      <p className="text-sm text-gray-500">
        Drag the handle on the left to reorder how images appear on the public site.
      </p>

      <SortableList
        items={gallery}
        onReorder={handleReorder}
        renderItem={(item) => (
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <GripVertical className="w-5 h-5 text-gray-400 cursor-move" />
              <img src={item.url} alt={item.title} className="w-16 h-16 object-cover rounded flex-shrink-0" />
              {editing?.id === item.id ? (
                <div className="flex-1 grid grid-cols-2 gap-2 min-w-0">
                  <Input
                    value={editing.title}
                    onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                    placeholder="Title"
                  />
                  <Select value={editing.category} onValueChange={(v) => setEditing({ ...editing, category: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold truncate">{item.title}</h3>
                  <p className="text-xs text-gray-500">{item.category}</p>
                </div>
              )}
              {editing?.id === item.id ? (
                <div className="flex gap-2">
                  <Button size="sm" onClick={saveEdit} className="bg-[#1565C0]">
                    <Check className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setEditing(null)}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <ItemActions
                  collection="gallery"
                  item={item}
                  onChanged={fetchGallery}
                  onEdit={setEditing}
                  onDelete={handleDelete}
                  onPreview={setPreviewing}
                />
              )}
            </CardContent>
          </Card>
        )}
      />
      {gallery.length === 0 && (
        <p className="text-center text-gray-500 py-8">No images yet. Drag-and-drop above to upload.</p>
      )}

      <PreviewModal kind="gallery" item={previewing} onClose={() => setPreviewing(null)} />
    </div>
  );
};

export default GalleryManager;
